import { motion } from "framer-motion";
import IconTile from "../IconTile";
import { ArrowRight, Sparkles, Mail } from "lucide-react";
import { FaWhatsapp, FaFacebookF, FaInstagram, FaLinkedinIn, FaGooglePlay } from "react-icons/fa";
import Navbar from "./Navbar";
import Footer from "./Footer";
import CredibilityStrip from "./CredibilityStrip";
import SEO from "../SEO";
import { FaqSection } from "../FaqList";
import { CONTACT_FAQS } from "../../data/pageFaqs";
import { Reveal } from "../Reveal";
import { SIGNUP_URL } from "../../data/content";
import { Button } from "../ui/button";

const IndiaFlagIcon = ({ className }) => (
  <span
    role="img"
    aria-label="Flag of India"
    className={`${className} flex items-center justify-center text-2xl leading-none`}
  >
    🇮🇳
  </span>
);

const CONTACT_CARDS = [
  {
    label: "Get Support",
    items: [
      { icon: Mail, text: "support@magicbolt.ai", href: "mailto:support@magicbolt.ai" },
      {
        icon: FaWhatsapp,
        text: "Chat on WhatsApp",
        href: "https://wa.me/916263206697?text=Hi%20MagicBolt%20AI%20Support%2C%20I%20need%20help%20with...",
        external: true,
      },
    ],
  },
  {
    icon: IndiaFlagIcon,
    label: "MagicBolt Private Limited",
    value: "Made in India",
    href: null,
  },
  {
    icon: FaWhatsapp,
    label: "Join WhatsApp Community",
    value: "Get product updates & talk to the team",
    href: "https://chat.whatsapp.com/KrxiKExv2Au8kXfvBRMfM9",
    external: true,
  },
];

const SOCIAL_LINKS = [
  { icon: FaFacebookF, label: "Facebook", href: "https://www.facebook.com/magicboltai/" },
  { icon: FaInstagram, label: "Instagram", href: "https://www.instagram.com/magicboltai/" },
  { icon: FaLinkedinIn, label: "LinkedIn", href: "https://www.linkedin.com/in/magicbolt/" },
  {
    icon: FaWhatsapp,
    label: "WhatsApp",
    href: "https://chat.whatsapp.com/KrxiKExv2Au8kXfvBRMfM9",
  },
  {
    icon: FaGooglePlay,
    label: "Google Play",
    href: "https://play.google.com/store/apps/details?id=com.magicbolt&hl=en_IN",
  },
];

export default function ContactPage() {
  return (
    <div className="min-h-screen bg-[#F4F6FB]" data-testid="contact-page">
      <SEO
        title="Contact Us - MagicBolt AI"
        description="Get in touch with MagicBolt Private Limited. Email support, WhatsApp support requests, and our WhatsApp community."
        path="/contact"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Contact", path: "/contact" },
        ]}
      />
      <Navbar />

      {/* Header */}
      <section className="relative pt-32 pb-16 overflow-hidden bg-white border-b border-[#E6E9F1]">
        <div className="mb-blob mb-blob-orange mb-anim-a w-[360px] h-[360px] -top-24 right-0" />
        <div className="mb-blob mb-blob-blue mb-anim-b w-[300px] h-[300px] top-10 -left-20" />
        <div className="absolute inset-0 mb-grid-lines opacity-60" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <img
              src="/logos/magicbolt.webp"
              alt="MagicBolt Private Limited"
              className="mx-auto mb-6 h-12 w-auto object-contain"
            />
            <div className="inline-flex items-center gap-2 rounded-full border border-[#E6E9F1] bg-white px-4 py-1.5 mb-6 shadow-[0_2px_10px_rgba(11,21,51,0.05)]">
              <Sparkles className="w-3.5 h-3.5 text-[#FF6B00]" />
              <span className="text-xs font-medium text-[#475569]">We're here to help</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-none text-[#0B1533]">
              Get in <span className="mb-gradient-text">touch</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#475569] max-w-2xl mx-auto leading-relaxed">
              Questions about MagicBolt AI, pricing, or white-label? Reach out - we reply fast.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact cards */}
      <section className="relative py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {CONTACT_CARDS.map((c, i) => {
              if (c.items) {
                return (
                  <Reveal key={c.label} delay={i * 0.1}>
                    <div
                      data-testid="contact-card"
                      className="h-full rounded-xl border border-[#E6E9F1] bg-white p-8 mb-card-shadow transition-transform duration-200 hover:-translate-y-1.5 hover:border-[#FF6B00]/40"
                    >
                      <h3 className="text-sm font-semibold uppercase tracking-wide text-[#94A3B8] mb-5">
                        {c.label}
                      </h3>
                      <div className="space-y-4">
                        {c.items.map((it) => (
                          <a
                            key={it.text}
                            href={it.href}
                            data-testid="contact-card-link"
                            {...(it.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                            className="group/item flex items-center gap-3"
                          >
                            <IconTile icon={it.icon} size="sm" tone="soft" className="group-hover/item:-translate-y-0.5 group-hover/item:scale-105" />
                            <span className="text-sm font-semibold text-[#0B1533] transition-colors group-hover/item:text-[#FF6B00]">
                              {it.text}
                            </span>
                          </a>
                        ))}
                      </div>
                    </div>
                  </Reveal>
                );
              }

              const CardInner = (
                <div
                  data-testid="contact-card"
                  className="group h-full rounded-xl border border-[#E6E9F1] bg-white p-8 mb-card-shadow transition-transform duration-200 hover:-translate-y-1.5 hover:border-[#FF6B00]/40"
                >
                  <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-[#FF6B00]/10 text-[#FF6B00] mb-6 transition-transform duration-300 group-hover:scale-110">
                    <c.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-[#94A3B8]">{c.label}</h3>
                  <p className="mt-3 text-base font-semibold text-[#0B1533] leading-relaxed">{c.value}</p>
                </div>
              );
              return (
                <Reveal key={c.label} delay={i * 0.1}>
                  {c.href ? (
                    <a
                      href={c.href}
                      data-testid="contact-card-link"
                      {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    >
                      {CardInner}
                    </a>
                  ) : (
                    CardInner
                  )}
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.3} className="mt-12 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#94A3B8] mb-5">
              Follow us on
            </p>
            <div className="flex justify-center gap-4">
              {SOCIAL_LINKS.map((s, i) => (
                <motion.a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  data-testid="social-link"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.3, delay: i * 0.06, ease: [0.23, 1, 0.32, 1] }}
                  className="grid place-items-center w-11 h-11 rounded-full border border-[#E6E9F1] bg-white text-[#475569] transition-[transform,border-color,color,box-shadow] duration-200 hover:-translate-y-1 hover:border-[#FF6B00]/40 hover:text-[#FF6B00] mb-card-shadow"
                >
                  <s.icon className="w-4 h-4" />
                </motion.a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 border-t border-[#E6E9F1] overflow-hidden bg-white">
        <div className="mb-blob mb-blob-orange mb-anim-a w-[360px] h-[360px] -top-20 left-1/2 -translate-x-1/2" />
        <div className="absolute inset-0 mb-radial-glow" />
        <div className="relative max-w-2xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tighter leading-none text-[#0B1533]">
              Ready to see MagicBolt AI in action?
            </h2>
            <p className="mt-4 text-base text-[#475569] leading-relaxed">
              Start your 14-day free trial - no credit card required.
            </p>
            <div className="mt-8 flex justify-center">
              <Button
                size="lg"
                className="bg-[#FF6B00] hover:bg-[#FF8533] text-white font-semibold rounded-lg h-12 px-7 text-base hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(255,107,0,0.35)]"
                asChild
              >
                <a href={SIGNUP_URL}>Start Free Trial <ArrowRight className="ml-2 w-4 h-4" /></a>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <FaqSection title="Contact & support questions" items={CONTACT_FAQS} />

      <CredibilityStrip />

      <Footer showCta={false} />
    </div>
  );
}
